import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { acquireToken } from '../auth/tokenProvider';
import { loginRequestScopes } from '../auth/scopes';
import {
  FLW_ORCHESTRATOR_URLS,
  FLW_API_PATHS,
  Environment,
  PolicyEvaluateRequest,
  PolicyEvaluateResponse,
  TenantGeoRegionResponse
} from '../models/flw-orchestrator.types';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Parses the WWW-Authenticate header to extract claims challenge
 * Format: Bearer error="insufficient_claims", claims="{"access_token":{...}}"
 * The claims value is a JSON string that may contain nested quotes
 */
function parseClaimsFromWWWAuthenticate(wwwAuthHeader: string | null): string | null {
  if (!wwwAuthHeader) return null;

  // Find the start of claims parameter
  const claimsIndex = wwwAuthHeader.indexOf('claims="');
  if (claimsIndex === -1) return null;

  // Start after 'claims="'
  const startIndex = claimsIndex + 'claims="'.length;
  let endIndex = startIndex;
  let braceCount = 0;
  let inBraces = false;

  // Parse until we find the closing quote, accounting for nested JSON braces
  while (endIndex < wwwAuthHeader.length) {
    const char = wwwAuthHeader[endIndex];

    if (char === '{') {
      braceCount++;
      inBraces = true;
    } else if (char === '}') {
      braceCount--;
      if (braceCount === 0) inBraces = false;
    } else if (char === '"' && !inBraces) {
      // Found closing quote outside of JSON braces
      break;
    }

    endIndex++;
  }

  const claimsValue = wwwAuthHeader.substring(startIndex, endIndex);
  console.log('[parseClaimsFromWWWAuthenticate] Extracted claims:', claimsValue);
  return claimsValue || null;
}

// Custom baseQuery that fetches an AAD token and injects region header
const orchestratorBaseQuery: BaseQueryFn<
  { environment: Environment; path: string; method?: string; data?: any; region?: string },
  unknown,
  FetchBaseQueryError
> = async ({ environment, path, method = 'GET', data, region }, api, extraOptions) => {
  console.log('[orchestratorBaseQuery] Starting request:', { path, method, region });
  try {
    const baseUrl = FLW_ORCHESTRATOR_URLS[environment];
    const fullUrl = region ? `${baseUrl}/${region}${path}` : `${baseUrl}${path}`;
    console.log('[orchestratorBaseQuery] Full URL:', fullUrl);

    // Acquire a single token for both login and FLW API scopes (no claims initially)
    const token = await acquireToken([...loginRequestScopes]);

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'Content-Type': method !== 'GET' ? 'application/json' : '',
      'Access-Control-Allow-Origin': '*'
    };
    if (region) {
      headers['TenantGeoRegion'] = region;
    }

    const fetchArgs: FetchArgs = {
      url: fullUrl,
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined
    };

    const result = await fetchBaseQuery()(fetchArgs, api, extraOptions);

    console.log('[orchestratorBaseQuery] Request completed, checking result:', {
      hasError: !!result.error,
      errorStatus: result.error?.status,
      hasData: !!result.data,
    });

    // Handle 401 with claims challenge (OBO token requires MFA step-up)
    if (result.error && result.error.status === 401) {
      console.log('[orchestratorBaseQuery] Got 401 error, checking for claims challenge');

      // Extract WWW-Authenticate header from the response
      const response = result.meta?.response;
      const wwwAuthHeader = response?.headers.get('Www-Authenticate') ?? null;
      console.log('[orchestratorBaseQuery] WWW-Authenticate header:', wwwAuthHeader);

      const claims = parseClaimsFromWWWAuthenticate(wwwAuthHeader);
      console.log('[orchestratorBaseQuery] Parsed claims:', claims ? claims.substring(0, 100) + '...' : 'none');

      if (claims) {
        console.log('[orchestratorBaseQuery] Claims challenge detected, acquiring new token with claims');
        console.log('[orchestratorBaseQuery] Original token (first 50 chars):', token.substring(0, 50));

        try {
          // Acquire a new token with the claims challenge
          const newToken = await acquireToken([...loginRequestScopes], claims);
          console.log('[orchestratorBaseQuery] New token acquired (first 50 chars):', newToken.substring(0, 50));
          console.log('[orchestratorBaseQuery] Tokens are same?', token === newToken);

          // Retry the request with the new token
          const retryHeaders = {
            ...headers,
            Authorization: `Bearer ${newToken}`
          };

          const retryFetchArgs: FetchArgs = {
            ...fetchArgs,
            headers: retryHeaders
          };

          console.log('[orchestratorBaseQuery] Retrying request with new token');
          const retryResult = await fetchBaseQuery()(retryFetchArgs, api, extraOptions);
          console.log('[orchestratorBaseQuery] Retry after claims challenge:', retryResult.error ? 'failed' : 'succeeded');
          return retryResult;
        } catch (claimsError) {
          console.error('[orchestratorBaseQuery] Failed to acquire token with claims:', claimsError);
          // Return original error if claims challenge fails
          return result;
        }
      } else {
        console.log('[orchestratorBaseQuery] No claims found in WWW-Authenticate header');
      }
    }

    return result;
  } catch (error) {
    // Token acquisition failed - return proper RTK Query error format
    return {
      error: {
        status: 'CUSTOM_ERROR' as const,
        error: error instanceof Error ? error.message : 'Token acquisition failed',
        data: error
      }
    };
  }
};

export const flwOrchestratorApi = createApi({
  reducerPath: 'flwOrchestratorApi',
  baseQuery: orchestratorBaseQuery,
  endpoints: builder => ({
    getTenantGeoRegion: builder.query<TenantGeoRegionResponse, Environment>({
      query: environment => ({ environment, path: FLW_API_PATHS.TENANT_GEO_REGION })
    }),
    evaluateMfa: builder.mutation<PolicyEvaluateResponse, { environment: Environment; payload: PolicyEvaluateRequest; region: string }>({
      query: ({ environment, payload, region }) => ({
        environment,
        path: FLW_API_PATHS.MFA_EVALUATE,
        method: 'POST',
        data: payload,
        region
      })
    }),
    evaluateMam: builder.mutation<PolicyEvaluateResponse, { environment: Environment; payload: PolicyEvaluateRequest; region: string }>({
      query: ({ environment, payload, region }) => ({
        environment,
        path: FLW_API_PATHS.MAM_EVALUATE,
        method: 'POST',
        data: payload,
        region
      })
    })
  })
});

export const {
  useGetTenantGeoRegionQuery,
  useEvaluateMfaMutation,
  useEvaluateMamMutation
} = flwOrchestratorApi;
